import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import './CustomDateTimePicker.scss';

interface CustomDateTimePickerProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({
    value,
    onChange,
    placeholder = "MM-DD-YYYY HH:MM",
    className = "",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string>("");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentView, setCurrentView] = useState<'date' | 'time'>('date');
    
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Initialize with current value
    useEffect(() => {
        if (value) {
            const parsedDate = moment(value, "MM-DD-YYYY HH:mm");
            if (parsedDate.isValid()) {
                setSelectedDate(parsedDate.toDate());
                setSelectedTime(parsedDate.format("HH:mm"));
            }
        }
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setCurrentView('date');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const today = new Date();
    const currentMonthDate = new Date(currentYear, currentMonth.getMonth(), 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth.getMonth() + 1, 0);
    const firstDayOfWeek = currentMonthDate.getDay();

    const isDateDisabled = (date: Date) => {
        return date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    };

    const handleDateSelect = (day: number) => {
        const selectedDateObj = new Date(currentYear, currentMonth.getMonth(), day);
        if (!isDateDisabled(selectedDateObj)) {
            setSelectedDate(selectedDateObj);
            setCurrentView('time');
        }
    };

    const handleTimeSelect = (time: string) => {
        setSelectedTime(time);
        if (selectedDate) {
            const combinedDateTime = moment(selectedDate).format("MM-DD-YYYY") + " " + time;
            onChange(combinedDateTime);
            setIsOpen(false);
            setCurrentView('date');
        }
    };

    const handleInputClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setCurrentView('date');
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Allow manual input in MM-DD-YYYY HH:MM format
        if (inputValue.length <= 16) {
            onChange(inputValue);
        }
    };

    const generateCalendarDays = () => {
        const days = [];
        const totalDays = lastDayOfMonth.getDate();

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        // Add days of the month
        for (let day = 1; day <= totalDays; day++) {
            const date = new Date(currentYear, currentMonth.getMonth(), day);
            const isDisabled = isDateDisabled(date);
            const isSelected = selectedDate && 
                selectedDate.getDate() === day && 
                selectedDate.getMonth() === currentMonth.getMonth() && 
                selectedDate.getFullYear() === currentYear;

            days.push(
                <div
                    key={day}
                    className={`calendar-day ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => !isDisabled && handleDateSelect(day)}
                >
                    {day}
                </div>
            );
        }

        return days;
    };

    const generateTimeSlots = () => {
        const times = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                times.push(
                    <div
                        key={timeString}
                        className={`time-slot ${selectedTime === timeString ? 'selected' : ''}`}
                        onClick={() => handleTimeSelect(timeString)}
                    >
                        {timeString}
                    </div>
                );
            }
        }
        return times;
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        const newMonth = new Date(currentMonth);
        if (direction === 'prev') {
            newMonth.setMonth(newMonth.getMonth() - 1);
        } else {
            newMonth.setMonth(newMonth.getMonth() + 1);
        }
        setCurrentMonth(newMonth);
        setCurrentYear(newMonth.getFullYear());
    };

    const handleMonthNavigation = (e: React.MouseEvent, direction: 'prev' | 'next') => {
        e.preventDefault();
        navigateMonth(direction);
    };

    const handleBackToDate = (e: React.MouseEvent) => {
        e.preventDefault();
        setCurrentView('date');
    };

    return (
        <div className={`custom-datetime-picker ${className}`} ref={wrapperRef}>
            <input
                type="text"
                value={value}
                onChange={handleInputChange}
                onClick={handleInputClick}
                placeholder={placeholder}
                className="datetime-input"
                disabled={disabled}
                readOnly
            />
            
            {isOpen && (
                <div className="datetime-dropdown">
                    {currentView === 'date' && (
                        <div className="date-picker">
                            <div className="calendar-header">
                                <button 
                                    className="nav-btn" 
                                    onClick={(e) => handleMonthNavigation(e, 'prev')}
                                    disabled={currentMonth.getMonth() === today.getMonth() && currentYear === today.getFullYear()}
                                >
                                    ‹
                                </button>
                                <span className="month-year">
                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </span>
                                <button className="nav-btn" onClick={(e) => handleMonthNavigation(e, 'next')}>
                                    ›
                                </button>
                            </div>
                            
                            <div className="calendar-weekdays">
                                <div>Sun</div>
                                <div>Mon</div>
                                <div>Tue</div>
                                <div>Wed</div>
                                <div>Thu</div>
                                <div>Fri</div>
                                <div>Sat</div>
                            </div>
                            
                            <div className="calendar-grid">
                                {generateCalendarDays()}
                            </div>
                        </div>
                    )}
                    
                    {currentView === 'time' && (
                        <div className="time-picker">
                            <div className="time-header">
                                <button className="back-btn" onClick={handleBackToDate}>
                                    ← Back to Date
                                </button>
                                <span className="selected-date">
                                    {selectedDate ? moment(selectedDate).format("MMM DD, YYYY") : ""}
                                </span>
                            </div>
                            
                            <div className="time-grid">
                                {generateTimeSlots()}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomDateTimePicker;
